import React from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Avatar,
  Tooltip,
  IconButton,
  alpha,
  useTheme,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfOutlined from "@mui/icons-material/PictureAsPdfOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import TableChartOutlined from "@mui/icons-material/TableChartOutlined";
import SlideshowOutlined from "@mui/icons-material/SlideshowOutlined";
import FolderZipOutlined from "@mui/icons-material/FolderZipOutlined";
import MovieOutlined from "@mui/icons-material/MovieOutlined";
import AudiotrackOutlined from "@mui/icons-material/AudiotrackOutlined";
import CodeOutlined from "@mui/icons-material/CodeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import useStyles from "../styles";

// Mapeamento de extensão -> ícone/cor, para identificar o tipo de arquivo
// nos anexos que não têm preview de imagem.
const FILE_TYPE_BY_EXT = {
  pdf: { icon: PictureAsPdfOutlined, color: "red" },
  doc: { icon: DescriptionOutlined, color: "blue" },
  docx: { icon: DescriptionOutlined, color: "blue" },
  odt: { icon: DescriptionOutlined, color: "blue" },
  txt: { icon: DescriptionOutlined, color: "blue" },
  rtf: { icon: DescriptionOutlined, color: "blue" },
  xls: { icon: TableChartOutlined, color: "green" },
  xlsx: { icon: TableChartOutlined, color: "green" },
  csv: { icon: TableChartOutlined, color: "green" },
  ppt: { icon: SlideshowOutlined, color: "orange" },
  pptx: { icon: SlideshowOutlined, color: "orange" },
  zip: { icon: FolderZipOutlined, color: "purple" },
  rar: { icon: FolderZipOutlined, color: "purple" },
  "7z": { icon: FolderZipOutlined, color: "purple" },
  tar: { icon: FolderZipOutlined, color: "purple" },
  gz: { icon: FolderZipOutlined, color: "purple" },
  mp4: { icon: MovieOutlined, color: "pink" },
  mov: { icon: MovieOutlined, color: "pink" },
  avi: { icon: MovieOutlined, color: "pink" },
  mkv: { icon: MovieOutlined, color: "pink" },
  webm: { icon: MovieOutlined, color: "pink" },
  mp3: { icon: AudiotrackOutlined, color: "yellow" },
  wav: { icon: AudiotrackOutlined, color: "yellow" },
  ogg: { icon: AudiotrackOutlined, color: "yellow" },
  json: { icon: CodeOutlined, color: "gray" },
  xml: { icon: CodeOutlined, color: "gray" },
  html: { icon: CodeOutlined, color: "gray" },
  js: { icon: CodeOutlined, color: "gray" },
};

function getFileTypeMeta(fileName) {
  const extension = fileName?.split(".")?.pop()?.toLowerCase() || "";
  return (
    FILE_TYPE_BY_EXT[extension] || {
      icon: InsertDriveFileOutlined,
      color: "gray",
    }
  );
}

function formatFileSize(bytes) {
  if (bytes === undefined || bytes === null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const FileDropzone = ({
  // Dados
  files = [],
  dragActive = false,
  inputId = "input-upload", // ID único caso use o componente 2x na mesma tela
  accept = "image/*,application/pdf,.doc,.docx,video/*",

  // Funções (Handlers)
  onDragOver = () => {},
  onDragLeave = () => {},
  onDrop = () => {},
  onFileChange = () => {}, // Do input
  onRemoveFile = () => {}, // Do botão X

  // Estilização
}) => {
  const theme = useTheme();
  const styles = useStyles(theme);

  const iconColor = (color, variant = "dark") => {
    if (variant === "dark") {
      return theme.palette.mode === "dark"
        ? theme.palette.colors[color].light
        : theme.palette.colors[color].dark;
    }
    return theme.palette.colors[color][variant];
  };

  return (
    <Box
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      sx={{
        ...styles.boxContainer,
        // Lógica visual baseada nas props recebidas
        border: dragActive
          ? `2px dashed ${theme.palette.primary.main}`
          : `2px dashed ${alpha(theme.palette.text.primary, 0.3)}`,
        backgroundColor: dragActive
          ? alpha(theme.palette.primary.main, 0.06)
          : "transparent",
        ...(files?.length === 0 && {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }),
      }}
    >
      <CloudUploadIcon sx={styles.boxContainerIcon} />
      <Typography color="text.secondary" sx={styles.textInfo} align="center">
        {dragActive
          ? "Solte os arquivos aqui"
          : "Arraste, cole (Ctrl+V) ou clique para selecionar"}
      </Typography>
      <Typography sx={styles.textInfoSecondary} align="center">
        Imagens, PDF, planilhas, documentos e vídeos
      </Typography>

      <input
        type="file"
        hidden
        multiple
        id={inputId}
        onChange={onFileChange}
        accept={accept}
      />

      <label htmlFor={inputId}>
        <Button
          variant="outlined"
          component="span"
          startIcon={<CloudUploadIcon />}
          sx={{ textTransform: "none", mb: 2, mt: 2 }}
        >
          Selecionar arquivos
        </Button>
      </label>

      {/* Grid de Arquivos */}
      {files.length > 0 ? (
        <>
          <Box sx={styles.filesHeader}>
            <Typography sx={styles.filesHeaderLabel}>
              {files.length}{" "}
              {files.length === 1
                ? "arquivo selecionado"
                : "arquivos selecionados"}
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {files.map((file, index) => {
              const isImage = file.type?.startsWith("image/");
              const preview = isImage ? URL.createObjectURL(file) : null;
              const { icon: FileIcon, color } = getFileTypeMeta(file?.name);

              return (
                <Grid item xs={4} sm={3} key={index}>
                  <Box sx={styles.boxImagePreview}>
                    {isImage ? (
                      <>
                        <Avatar
                          src={preview}
                          alt={file.name}
                          variant="rounded"
                          sx={styles.avatar}
                        />
                        <Box sx={styles.imageCaptionOverlay}>
                          <Typography
                            sx={styles.imageCaptionText}
                            title={file?.name}
                          >
                            {file?.name}q
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <Tooltip title={file?.name || ""}>
                        <Box sx={styles.containerCard}>
                          <Box
                            sx={{
                              ...styles.fileIconBadge,
                              backgroundColor: alpha(
                                iconColor(color, "medium"),
                                0.15,
                              ),
                            }}
                          >
                            <FileIcon
                              sx={{
                                fontSize: "24px",
                                color: iconColor(color),
                              }}
                            />
                          </Box>
                          <Box sx={styles.boxTitleAnexo}>
                            <Typography sx={styles.boxTextAnexo} noWrap>
                              {file?.name}
                            </Typography>
                          </Box>
                          <Typography sx={styles.fileSizeCaption}>
                            {formatFileSize(file?.size)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}

                    <IconButton
                      size="small"
                      onClick={() => onRemoveFile(index)}
                      sx={styles.iconButton}
                    >
                      <CloseIcon
                        sx={{
                          fontSize: "14px",
                          color: theme.palette.common.white,
                        }}
                      />
                    </IconButton>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </>
      ) : (
        <Typography
          color="text.secondary"
          sx={{ ...styles.textInfo, fontSize: "0.8rem", mt: 1 }}
        >
          Nenhum arquivo selecionado
        </Typography>
      )}
    </Box>
  );
};

export default FileDropzone;
